import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { SubscriptionHandle } from '@jack-henry/frontend-utils/observable';
import { LoggingService } from '@treasury/core/logging';
import { Feature, FeatureFlagService } from '@treasury/domain/services/feature-flags';
import {
    CheckCaptureService,
    MitekAutoCaptureHints,
    MitekCaptureResult,
    MitekCaptureStatus,
} from '@treasury/libraries';
import { TmBaseComponent } from '@treasury/presentation';
import { closeIcon, informationIcon } from '@treasury/presentation/assets/icons';
import { LocalStorageService } from '@treasury/utils/services';
import { MediaService } from '@treasury/utils/services/media-service';
import { css, html, nothing, render } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import { DeviceService } from '@treasury/domain/services/device/device.service';
import '@treasury/presentation/components/tm-tooltip-rich';

export const tagName = 'tm-check-capture';
/**
 * @fires TmCheckCapture#image-capture-failed - Event fired when the image capture fails.
 * @fires TmCheckCapture#switch-to-manual - Event fired when the user wants to switch to manual capture.
 */
@customElement(tagName)
export class TmCheckCapture extends TmBaseComponent {
    @property({ type: Boolean, reflect: true })
    public captureOnStart = true;

    @property({ type: String, reflect: true })
    public checkSide: 'front' | 'back' = 'front';

    @property({ attribute: false })
    public photo?: string | null;

    @InjectProperty()
    declare private checkCaptureService: CheckCaptureService;

    @InjectProperty()
    declare private mediaService: MediaService;

    @InjectProperty({ async: true, token: FeatureFlagService })
    declare private readonly featureFlagServicePromise: Promise<FeatureFlagService>;

    @InjectProperty()
    declare private readonly logService: LoggingService;

    @InjectProperty()
    declare private readonly localStorageService: LocalStorageService;

    @InjectProperty()
    declare private readonly deviceService: DeviceService;

    @state()
    private isCapturing = false;

    @state()
    private shouldShowManualCaptureButton = false;

    private displayStartedSubscription?: SubscriptionHandle;

    private displayStoppedSubscription?: SubscriptionHandle;

    private captureSubscription?: SubscriptionHandle;

    private sdkErrorSubscription?: SubscriptionHandle;

    private captureStartErrorTimer?: NodeJS.Timeout;

    private isManualCaptureEnabled = false;

    private forceManualCapture = false;

    public connectedCallback(): void {
        super.connectedCallback();
        this.sdkErrorSubscription = this.checkCaptureService.sdkError$.subscribe(async error => {
            this.dispatchEvent(new CustomEvent('image-capture-failed', { detail: error }));
            await this.stopCapture();
        });
        this.captureSubscription = this.checkCaptureService.captureCompleted$.subscribe(
            async (result: MitekCaptureResult) => {
                await this.stopCapture(result.response.mode);
                this.shouldShowManualCaptureButton = false;

                const { error, failedImage, imageData, docType, status } = result.response;

                const capStatus = status === MitekCaptureStatus.SUCCESS ? 'completed' : 'failed';
                const logDetail = { capture_step: 'Capture', check_side: this.checkSide };
                this.logService.logInfo(`tm-check-capture capture ${capStatus}`, logDetail);

                const capturedImage = failedImage || imageData;

                if (error || !capturedImage) {
                    this.dispatchEvent(new CustomEvent('image-capture-failed', { detail: result }));
                    return;
                }

                if (this.docTypeMatches(docType)) {
                    this.photo =
                        result.response.mode === 'MANUAL'
                            ? await this.rotateImage(capturedImage, 180)
                            : capturedImage;
                    this.dispatchEvent(
                        new CustomEvent('image-uploaded', {
                            detail: {
                                checkSide: this.checkSide,
                                image: this.photo,
                            },
                        })
                    );
                }
            },
            false
        );
        this.displayStartedSubscription = this.checkCaptureService.displayStarted$.subscribe(
            async documentType => {
                this.clearCaptureStartErrorTimer();
                if (this.docTypeMatches(documentType)) {
                    this.logService.logInfo('tm-check-capture display started', {
                        capture_step: 'Capture',
                        check_side: this.checkSide,
                    });
                    this.addOverlay();
                    this.checkCaptureService.showHint(
                        MitekAutoCaptureHints.AUTO_CAPTURE_HINT,
                        3000
                    );
                }
            }
        );
        this.displayStoppedSubscription = this.checkCaptureService.displayStopped$.subscribe(
            async () => {
                this.logService.logInfo('tm-check-capture display stopped', {
                    capture_step: 'Capture',
                    check_side: this.checkSide,
                });
            }
        );
    }

    public disconnectedCallback(): void {
        super.disconnectedCallback();
        this.sdkErrorSubscription?.unsubscribe();
        this.displayStartedSubscription?.unsubscribe();
        this.displayStoppedSubscription?.unsubscribe();
        this.captureSubscription?.unsubscribe();
    }

    public async firstUpdated() {
        this.forceManualCapture =
            this.localStorageService.get<boolean>('remoteDepositManualCaptureOnly') ?? false;

        if (this.captureOnStart) {
            if (this.deviceService.isSafari && this.forceManualCapture) {
                this.shouldShowManualCaptureButton = true;
            } else {
                this.startCapture();
            }
        }
        const ffService = await this.featureFlagServicePromise;
        this.isManualCaptureEnabled = await ffService.isEnabled(Feature.RemoteDepositManualCapture);
    }

    public async stopCapture(mode: MitekCaptureResult['response']['mode'] = 'AUTO') {
        this.logService.logInfo('tm-check-capture stopping capture', {
            capture_step: 'Capture',
            check_side: this.checkSide,
        });
        this.clearCaptureStartErrorTimer();
        this.removeOverlay();
        await this.checkCaptureService.stopCapture(mode);
        this.isCapturing = false;
        return;
    }

    public async startCapture(isManual = false) {
        // Check if manual capture is forced via user preference
        const shouldUseManual = isManual || this.forceManualCapture;

        this.isCapturing = true;
        // Only start capture error timer for auto captures
        if (!shouldUseManual) {
            this.captureStartErrorTimer = setTimeout(() => {
                this.logService.logError('Remote Deposit Error - check capture start timed out');
                this.dispatchEvent(
                    new CustomEvent('camera-start-timeout', {
                        detail: new Error(
                            'Check capture failed to start. Please try again. If you are still unable to start a capture session please contact customer service.'
                        ),
                    })
                );
            }, 30000);
        }
        try {
            this.checkCaptureService.startCapture(
                this.checkSide === 'front' ? 'CHECK_FRONT' : 'CHECK_BACK',
                shouldUseManual ? 'MANUAL_CAPTURE' : 'AUTO_CAPTURE'
            );
        } catch (error) {
            this.dispatchEvent(new CustomEvent('image-capture-failed', { detail: error }));
        }
    }

    protected render() {
        return html` <button
                class="output relative text-center flex items-center justify-between grow align-top"
                @click=${() => {
                    if (!this.captureOnStart) {
                        this.startCapture();
                    }
                }}
            >
                ${this.renderPhoto()}</button
            >${this.renderManualCapture()}`;
    }

    private clearCaptureStartErrorTimer() {
        if (this.captureStartErrorTimer) {
            clearTimeout(this.captureStartErrorTimer);
            this.captureStartErrorTimer = undefined;
        }
    }

    private removeOverlay() {
        const overlayControls = document.querySelector('#mitekOverlayControls');
        overlayControls?.remove();
    }

    private docTypeMatches(documentType: MitekCaptureResult['response']['docType']) {
        const bothFront = documentType === 'CHECK_FRONT' && this.checkSide === 'front';
        const bothBack = documentType === 'CHECK_BACK' && this.checkSide === 'back';
        return bothFront || bothBack;
    }

    private async rotateImage(base64Image: string, degrees: number): Promise<string> {
        return new Promise(resolve => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();

            img.onload = () => {
                // Set canvas dimensions based on rotation
                if (degrees === 90 || degrees === 270) {
                    canvas.width = img.height;
                    canvas.height = img.width;
                } else {
                    canvas.width = img.width;
                    canvas.height = img.height;
                }

                if (ctx) {
                    // Clear the canvas
                    ctx.clearRect(0, 0, canvas.width, canvas.height);

                    // Move to center of canvas
                    ctx.translate(canvas.width / 2, canvas.height / 2);

                    // Rotate by the specified degrees
                    ctx.rotate((degrees * Math.PI) / 180);

                    // Draw the image centered
                    ctx.drawImage(img, -img.width / 2, -img.height / 2);

                    // Convert back to base64
                    resolve(canvas.toDataURL('image/jpeg', 0.9));
                } else {
                    resolve(base64Image);
                }
            };

            img.onerror = () => {
                // If image fails to load, return original
                resolve(base64Image);
            };

            img.src = base64Image;
        });
    }

    private async close(isManual = false) {
        if (!isManual) {
            await this.stopCapture();
        }
        this.dispatchEvent(new CustomEvent('close'));
    }

    private overlayExists() {
        return document.querySelector('#mitekOverlayControls');
    }

    private addOverlay() {
        if (this.overlayExists()) {
            return;
        }
        const mitekDisplayContainer = document.querySelector('#mitekDisplayContainer');

        const overlayContainer = document.createElement('div');
        overlayContainer.setAttribute('id', 'mitekOverlayControls');
        overlayContainer.setAttribute(
            'style',
            'position: absolute; right: 0px; top: 0px; z-index: 101; background-color: transparent;'
        );
        const overlayControls = this.renderOverlayControls();
        render(overlayControls, overlayContainer);
        if (mitekDisplayContainer) {
            mitekDisplayContainer.appendChild(overlayContainer);
        }
    }

    private renderBackTipWarning() {
        if (this.checkSide !== 'back') {
            return nothing;
        }
        return html`<div
            class="flex justify-center items-center mt-3 mb-3 p-2 text-sm border rounded-lg border-[--content-attention]"
        >
            <div>
                <span class="font-bold">“For Mobile Deposit Only”</span> or the Restricted
                Endorsement required by your financial institution must be written clearly below
                your signature.
            </div>
        </div>`;
    }

    private renderHintTooltip(isManual = false) {
        const hintText = isManual
            ? html`Place check on a flat, well-lit, dark surface and hold still with the check
              within the frame when capturing. `
            : html`Place check on a flat, well-lit, dark surface and hold still with the check
                  within the frame to auto-capture. <br /><br />
                  If there are issues with auto-capture, try manual capture.`;
        return html`<div
            class="capture-hint-header flex flex-col items-left pb-1 font-medium text-[--header-text-color] border-[--border-color]"
        >
            <div>
                <span class="text-lg">Capture check ${this.checkSide}</span>
                <div class="font-normal text-sm">${hintText}</div>
            </div>
            ${this.renderBackTipWarning()}
            <div class="flex justify-end items-right pt-2">
                <img
                    class="w-1/3"
                    src="/pwa/assets/images/graphic-check-orientation-${this.checkSide}.png"
                    alt="Check ${this.checkSide} sample"
                />
            </div>
        </div>`;
    }

    private renderManualCaptureButton() {
        if (!this.isManualCaptureEnabled || this.forceManualCapture) {
            return nothing;
        }
        const tapToCapturePrompt = 'In cases of auto-capture failure ';

        return html`<button
            id="manualCaptureNotice"
            class="fixed text-center flex justify-center items-center portrait:bottom-3 landscape:bottom-1 left-2 right-2 bg-transparent portrait:text-sm text-balance text-gray-100 portrait:leading-none"
            @click=${async () => {
                await this.stopCapture();
                if (!this.deviceService.isSafari) {
                    this.startCapture(true);
                } else {
                    this.shouldShowManualCaptureButton = true;
                }
            }}
        >
            ${tapToCapturePrompt}
            <span
                class="border-[--tm-color-gray-light-30] bg-[--tm-color-gray-medium-15] border rounded-md ml-2 px-2 py-1"
                >Capture Manually</span
            >
        </button>`;
    }

    private renderOverlayControls(isManual = false) {
        const checkSideText = this.checkSide === 'front' ? 'Front' : 'Back';

        return html`
            <div
                id="top-controls"
                class="relative text-center flex items-center justify-between p-2 w-screen landscape:-mt-3 pt-[--safe-area-top]"
            >
                <button class="close-button mt-2" @click=${() => this.close(isManual)}>
                    <tm-icon color="#ccc" .icon=${closeIcon}></tm-icon>
                </button>
                <div class="check-side-label text-gray-300 text-xl">${checkSideText} of Check</div>
                <tm-tooltip-rich direction="bottom-left" isClickable>
                    <tm-icon class="mt-2" color="#ccc" .icon=${informationIcon}></tm-icon>

                    <style>
                        .tooltip-content {
                            width: 328px;
                            margin-top: -7px;
                            color: var(--primary-text-color);
                        }
                    </style>
                    <div class="tooltip-content" slot="content">
                        ${this.renderHintTooltip(isManual)}
                    </div>
                </tm-tooltip-rich>
            </div>

            ${this.renderManualCaptureButton()}
        `;
    }

    private renderPhoto() {
        const renderCaptureLoading = this.isCapturing
            ? html`<tm-loader card></tm-loader>`
            : nothing;

        if (this.photo) {
            return html`
                <img
                    class="photo border shadow"
                    src=${this.photo}
                    alt="Captured ${this.checkSide} image"
                />
                ${renderCaptureLoading}
            `;
        }
        return html` <div class="empty-photo border shadow">${renderCaptureLoading}</div>`;
    }

    private renderManualCapture() {
        const manualCaptureWording = this.forceManualCapture
            ? 'Capture Front of Check'
            : 'Start Manual Capture';
        if (this.shouldShowManualCaptureButton) {
            return html` <div
                class="fixed inset-0 w-screen h-screen flex flex-col justify-start items-center bg-black bg-opacity-75 z-50"
            >
                ${this.renderOverlayControls(true)}
                <div class="flex-grow flex flex-col justify-center">
                    <button
                        class=" justify-self-center px-6 py-3 border border-white rounded text-white hover:bg-white hover:text-black transition-colors"
                        @click=${() => {
                            this.startCapture(true);
                        }}
                    >
                        ${manualCaptureWording}
                    </button>
                </div>
            </div>`;
        }
        return nothing;
    }

    public static get styles() {
        return [
            css`
                .output {
                    width: 140px;
                    --tm-loader-color: white;
                }

                .photo,
                .empty-photo {
                    width: 140px;
                }
                .empty-photo {
                    min-height: 140px;
                    width: 100%;
                }

                :host([captureOnStart]),
                :host([captureOnStart]) .output,
                :host([captureOnStart]) .photo {
                    width: 100%;
                    height: 100%;
                    flex-grow: 1;
                }

                #portrait-warning {
                    background: linear-gradient(
                        159deg,
                        #ccc4b8 26.89%,
                        #c2b39c 41.6%,
                        #ac9d86 70.38%
                    );
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: TmCheckCapture;
    }
}
